import { globalConfirm } from '../../components/ConfirmDialog';
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { EmptyState } from '../../components/layout/SharedComponents';
import { SortableTableHeader, useSortState, sortData } from '../../components/SortableTableHeader';
import LineItemDetailEditor from '../Requisitions/components/LineItemDetailEditor';
import AddCatalogItemModal from '../Requisitions/modals/AddCatalogItemModal';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import * as XLSX from 'xlsx';
import {
  ChevronsDown, X, Trash2, Copy, Plus, Search, ListOrdered,
  FileSpreadsheet, Download, ShoppingCart, Package
} from 'lucide-react';

export function POLineItemsPanel({ order, orderNumber, canEdit, fmtVal, fmtTotal, currency, onReload, mandatoryLineFields, onSaveHeader }) {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [actionsOpen, setActionsOpen] = useState(false);
  const [amendNotes, setAmendNotes] = useState('');
  const [showAmend, setShowAmend] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [addingItem, setAddingItem] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [punchoutUrl, setPunchoutUrl] = useState(null);
  const [punchoutLoading, setPunchoutLoading] = useState(false);
  const fileInputRef = useRef(null);
  const actionsRef = useRef(null);
  const status = (order.status || '').toUpperCase();
  const isPunchout = order.is_punchout || order.punchout_details?.enabled;

  const [poLineSort, handlePoLineSort] = useSortState('', 'asc');

  useEffect(() => {
    const handler = async (e) => {
      if (e.data?.type === 'punchout-cart' && e.data.order === orderNumber) {
        const items = e.data.items || [];
        for (const item of items) {
          try {
            await api.post(`/api/orders/${orderNumber}/line-items`, {
              item_code: item.item_code || item.supplier_part_number || '',
              item_description: item.item_description || '',
              quantity: item.quantity || 1,
              unit: item.unit || 'EACH',
              unit_price: item.unit_price || 0,
              pack_size: 1,
              discount: 0,
              suppliers_part_number: item.supplier_part_number || '',
            });
          } catch {}
        }
        toast.success(`${items.length} punchout item(s) added to order — continue shopping or close the catalog`);
        onReload();
      } else if (e.data?.type === 'punchout-error' && e.data.order === orderNumber) {
        toast.error(e.data.error || 'Punchout failed');
        setPunchoutUrl(null);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [orderNumber, onReload]);

  useEffect(() => {
    const h = e => { if (actionsRef.current && !actionsRef.current.contains(e.target)) setActionsOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const TRANSITIONS = {
    UNISSUED:  ['APPROVED', 'CANCELLED'],
    PENDING:   ['CANCELLED'],
    APPROVED:  ['CANCELLED'],
    ISSUED:    ['AMENDED', 'PARTIAL', 'COMPLETED', 'CANCELLED'],
    AMENDED:   ['UNISSUED', 'CANCELLED'],
    PARTIAL:   ['COMPLETED'],
    COMPLETED: ['CLOSED'],
  };
  const validNext = TRANSITIONS[status] || [];

  const changeStatus = async (newStatus) => {
    if (newStatus === 'AMENDED') {
      if (status === 'PENDING') {
        toast.error('Cannot amend a purchase order that is pending approval. Cancel it first, then copy it to a new order and edit the new one.');
        setShowStatusMenu(false);
        return;
      }
      setShowAmend(true); setShowStatusMenu(false); return;
    }
    if (!await globalConfirm(`Change order status to ${newStatus}?`)) return;
    try {
      await api.post(`/api/orders/${orderNumber}/status`, { status: newStatus });
      toast.success(`Status changed to ${newStatus}`);
      onReload();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Status change failed')); }
    setShowStatusMenu(false);
  };

  const submitAmendment = async () => {
    if (!amendNotes.trim()) { toast.error('Amendment reason is required'); return; }
    try {
      await api.post(`/api/orders/${orderNumber}/status`, { status: 'AMENDED', notes: amendNotes });
      toast.success('Order amended — returned to Unissued for editing');
      setShowAmend(false); setAmendNotes('');
      onReload();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Amendment failed')); }
  };

  const deleteItem = async (itemId) => {
    if (!await globalConfirm('Delete this line item?')) return;
    try { await api.delete(`/api/orders/${orderNumber}/line-items/${itemId}`); toast.success('Deleted'); onReload(); }
    catch { toast.error('Delete failed'); }
  };

  const addItem = async (item) => {
    try {
      item.net_price = parseFloat(((parseFloat(item.quantity)||0) * (parseFloat(item.unit_price)||0) * (1 - (parseFloat(item.discount)||0)/100)).toFixed(2));
      await api.post(`/api/orders/${orderNumber}/line-items`, item);
      toast.success('Item added');
      setAddingItem(false);
      onReload();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to add item')); }
  };

  const launchPunchout = async () => {
    setActionsOpen(false);
    setPunchoutLoading(true);
    try {
      const { id, _id, order_number, created_at, updated_at, line_items, counts, ...payload } = order;
      await api.put(`/api/orders/${orderNumber}`, payload);
    } catch {}
    try {
      const r = await api.post(`/api/punchout/setup/${orderNumber}`);
      setPunchoutUrl(r.data.start_url);
      toast.success('Punchout catalog opened — shop and checkout to add items');
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to launch punchout catalog')); }
    setPunchoutLoading(false);
  };

  const addCatalogItem = async (item) => {
    try {
      const newItem = {
        item_code: item.item_code || '',
        item_description: item.description || item.item_description || '',
        quantity: 1,
        unit: item.unit || 'EACH',
        pack_size: item.pack_size || 1,
        unit_price: parseFloat(item.price || item.unit_price || 0),
        discount: 0,
        supplier_code: item.supplier_code || order.supplier_code || '',
        net_price: parseFloat(item.price || item.unit_price || 0),
      };
      await api.post(`/api/orders/${orderNumber}/line-items`, newItem);
      toast.success(`Added: ${newItem.item_description}`);
      onReload();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to add catalog item')); }
  };

  const reindexItems = async () => {
    try {
      await api.post(`/api/orders/${orderNumber}/reindex`);
      toast.success('Items re-indexed');
      onReload();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Re-index failed')); }
    setReorderOpen(false);
  };

  const downloadExcelTemplate = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet([
      { 'Item Code': '', 'Item Description': 'Sample item', 'Quantity': 1, 'Unit': 'EACH', 'Pack Size': 1, 'Unit Price': 0, 'Discount': 0, 'Supplier Code': '', 'Item Due Date': '' }
    ]);
    XLSX.utils.book_append_sheet(wb, ws, 'Line Items');
    XLSX.writeFile(wb, `PO_${orderNumber}_Template.xlsx`);
    toast.success('Template downloaded');
  };

  const deleteAllItems = async () => {
    if (!await globalConfirm(`Delete ALL ${(order.line_items||[]).length} line items?`)) return;
    try {
      for (const item of (order.line_items || [])) {
        await api.delete(`/api/orders/${orderNumber}/line-items/${item.line_number || item.id}`);
      }
      toast.success('All items deleted');
      onReload();
    } catch { toast.error('Delete failed'); }
  };

  const ACTIONS = [
    { icon: Plus, label: 'Add New', action: () => { setAddingItem(true); setActionsOpen(false); } },
    { icon: ShoppingCart, label: 'Add from PunchOut Catalog', action: launchPunchout, highlight: isPunchout },
    { icon: Search, label: 'Add Catalog Item', action: () => { setCatalogOpen(true); setActionsOpen(false); } },
    { icon: ListOrdered, label: 'Re-Index', action: () => { reindexItems(); setActionsOpen(false); } },
    { icon: FileSpreadsheet, label: 'Import from MS-Excel', action: () => { fileInputRef.current?.click(); setActionsOpen(false); } },
    { icon: Download, label: 'Download Excel Template', action: () => { downloadExcelTemplate(); setActionsOpen(false); } },
    { icon: Trash2, label: 'Delete All', action: () => { deleteAllItems(); setActionsOpen(false); }, danger: true },
  ];

  const updateItem = async (itemId, data) => {
    try {
      await api.put(`/api/orders/${orderNumber}/line-items/${itemId}`, data);
      toast.success('Item updated');
      await onReload();
      setEditingItemId(null);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post(`/api/orders/${orderNumber}/import-lines`, fd);
      toast.success(r.data.message);
      onReload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    e.target.value = '';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 relative" ref={actionsRef}>
          {canEdit && (
            <>
              <button onClick={() => setActionsOpen(v => !v)}
                className="flex items-center gap-1.5 font-bold text-primary hover:opacity-80"
                aria-expanded={actionsOpen} aria-label="Line Items actions">
                <ChevronsDown className="h-5 w-5 text-orange-500" strokeWidth={3} /> Line Items - Main Details
              </button>
              {actionsOpen && (
                <div className="absolute top-full left-0 mt-1 z-50 bg-card border rounded-xl shadow-xl p-4 w-56">
                  <p className="font-bold text-sm mb-2">Actions</p>
                  {ACTIONS.map((a, i) => (
                    <button key={i} onClick={a.action}
                      className={`flex items-center gap-2.5 w-full text-left text-sm py-2 border-b last:border-0 hover:text-primary transition-colors ${a.danger ? 'text-destructive' : ''}`}>
                      <a.icon className="h-4 w-4 flex-shrink-0" />
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          {!canEdit && <h3 className="font-bold text-primary">Line Items - Main Details</h3>}
        </div>
        <div className="flex items-center gap-2">
        </div>
      </div>

      <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={importExcel} />

      {addingItem && (
        <LineItemDetailEditor
          item={(() => {
            const lines = order.line_items || [];
            const lastLine = lines.length > 0 ? lines[lines.length - 1] : null;
            return {
              item_code: '', item_description: '', item_due_date: '', quantity: 1, unit: 'EACH', pack_size: 1, discount: 0, unit_price: 0, supplier_code: '', suppliers_part_number: '', sales_tax: 0, expense_type: lastLine?.expense_type || 'None', co2e: 0,
              account_code_4: lastLine?.account_code_4 || '', account_code_5: lastLine?.account_code_5 || '', account_code_6: lastLine?.account_code_6 || '',
            };
          })()}
          requestNumber={orderNumber}
          onSave={addItem}
          onCancel={() => setAddingItem(false)}
          isNew
          mandatoryLineFields={mandatoryLineFields || []}
          hideSupplier
        />
      )}

      {catalogOpen && (
        <AddCatalogItemModal open={catalogOpen} onClose={() => setCatalogOpen(false)} onAdd={addCatalogItem} />
      )}

      {punchoutUrl && (
        <div className="mb-3 border-2 border-purple-300 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-purple-50 border-b border-purple-200">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-semibold text-purple-700">Punchout Catalog — {order.supplier_name || order.supplier_code}</span>
            </div>
            <Button size="sm" variant="outline" className="h-7 text-xs border-purple-300 text-purple-600" onClick={() => setPunchoutUrl(null)}>
              <X className="h-3 w-3 mr-1" /> Close Catalog
            </Button>
          </div>
          <iframe
            src={punchoutUrl}
            className="w-full border-0"
            style={{ height: '600px' }}
            title="Supplier Punchout Catalog"
          />
        </div>
      )}
      {punchoutLoading && (
        <div className="mb-3 p-6 border-2 border-purple-200 rounded-lg bg-purple-50 text-center">
          <ShoppingCart className="h-8 w-8 text-purple-400 mx-auto mb-2 animate-pulse" />
          <p className="text-sm text-purple-600 font-semibold">Connecting to supplier catalog...</p>
        </div>
      )}

      {showAmend && (
        <div className="mb-3 p-4 border border-amber-300 rounded-lg bg-amber-50">
          <p className="text-sm font-semibold text-amber-700 mb-2">Amend Order</p>
          <p className="text-xs text-amber-600 mb-2">This will increment the version number and return the order to UNISSUED for editing. Please provide the reason:</p>
          <Textarea value={amendNotes} onChange={e => setAmendNotes(e.target.value)} rows={2} placeholder="Reason for amendment..." className="text-sm mb-2" />
          <div className="flex gap-2">
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={submitAmendment}>Confirm Amendment</Button>
            <Button size="sm" variant="outline" onClick={() => { setShowAmend(false); setAmendNotes(''); }}>Cancel</Button>
          </div>
        </div>
      )}

      {order.version > 1 && (
        <div className="mb-2 text-xs text-muted-foreground">Amendment: <strong>{order.version - 1}</strong> {order.amendment_date && `(amended ${new Date(order.amendment_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz })})`}</div>
      )}

      {(order.line_items||[]).length === 0 ? <EmptyState title="No line items" /> : (
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-muted/50">
              <SortableTableHeader label="Item" field="item_code" sortState={poLineSort} onSort={handlePoLineSort} />
              <SortableTableHeader label="Description" field="description" sortState={poLineSort} onSort={handlePoLineSort} />
              <SortableTableHeader label="Qty" field="quantity" sortState={poLineSort} onSort={handlePoLineSort} className="text-right" />
              <SortableTableHeader label="Unit" field="unit_of_measure" sortState={poLineSort} onSort={handlePoLineSort} />
              <SortableTableHeader label="Pack" field="pack_size" sortState={poLineSort} onSort={handlePoLineSort} className="text-right" />
              <SortableTableHeader label="Disc%" field="discount" sortState={poLineSort} onSort={handlePoLineSort} className="text-right" />
              <SortableTableHeader label="Tax%" field="sales_tax" sortState={poLineSort} onSort={handlePoLineSort} className="text-right" />
              <SortableTableHeader label={`Price (${currency})`} field="unit_price" sortState={poLineSort} onSort={handlePoLineSort} className="text-right" />
              <SortableTableHeader label="Net" field="net_price" sortState={poLineSort} onSort={handlePoLineSort} className="text-right" />
              <SortableTableHeader label={`Tax Amt (${currency})`} field="_tax_amt" sortState={poLineSort} onSort={handlePoLineSort} className="text-right" />
              <SortableTableHeader label={`Gross (${currency})`} field="_gross" sortState={poLineSort} onSort={handlePoLineSort} className="text-right" />
              <SortableTableHeader label="Due Date" field="due_date" sortState={poLineSort} onSort={handlePoLineSort} />
              {canEdit && <th className="w-8 border-b"></th>}
            </tr>
          </thead>
          <tbody>
            {sortData((order.line_items||[]).map(item => ({...item, _tax_amt: Math.round((item.net_price||0) * ((item.sales_tax||0)/100) * 100) / 100, _gross: (item.net_price||0) + Math.round((item.net_price||0) * ((item.sales_tax||0)/100) * 100) / 100})), poLineSort.field, poLineSort.dir).map((item, idx) => {
              const net = item.net_price || 0;
              const taxAmt = Math.round(net * ((item.sales_tax||0)/100) * 100) / 100;
              const gross = net + taxAmt;
              return (
                <React.Fragment key={item.id || idx}>
                  <tr className={`border-b hover:bg-accent/20 cursor-pointer ${editingItemId === item.id ? 'bg-primary/5' : ''}`}
                    onClick={() => setEditingItemId(editingItemId === item.id ? null : item.id)}>
                    <td className="px-2 py-1.5 w-10">{item.item_num || idx+1}</td>
                    <td className="px-2 py-1.5 font-medium max-w-[220px] truncate">{item.item_description}</td>
                    <td className="px-2 py-1.5 text-right w-24">{fmtVal(item.quantity)}</td>
                    <td className="px-2 py-1.5">{item.unit}</td>
                    <td className="px-2 py-1.5 text-right">{item.pack_size||1}</td>
                    <td className="px-2 py-1.5 text-right">{fmtVal(item.discount||0)}</td>
                    <td className="px-2 py-1.5 text-right">{fmtVal(item.sales_tax||0)}</td>
                    <td className="px-2 py-1.5 text-right">{fmtVal(item.unit_price)}</td>
                    <td className="px-2 py-1.5 text-right font-semibold text-primary">{fmtTotal(net)}</td>
                    <td className="px-2 py-1.5 text-right">{fmtTotal(taxAmt)}</td>
                    <td className="px-2 py-1.5 text-right font-semibold">{fmtTotal(gross)}</td>
                    <td className="px-2 py-1.5 w-28 whitespace-nowrap">{item.item_due_date ? new Date(item.item_due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : ''}</td>
                    {canEdit && <td className="px-1" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <button onClick={() => {
                          const copy = { ...item };
                          delete copy.id; delete copy.line_number;
                          copy.item_num = Math.max(0, ...(order.line_items || []).map(li => li.item_num || 0)) + 1;
                          addItem(copy);
                        }} className="text-blue-600 hover:opacity-70" title="Copy line item"><Copy className="h-3.5 w-3.5" /></button>
                        <button onClick={() => deleteItem(item.id)} className="text-destructive hover:opacity-70" title="Delete line item"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>}
                  </tr>
                  {editingItemId === item.id && (
                    <tr><td colSpan={canEdit ? 13 : 12} className="p-0">
                      <LineItemDetailEditor
                        item={item}
                        requestNumber={orderNumber}
                        onSave={canEdit ? async (d) => { await updateItem(item.id, d); } : null}
                        onDelete={canEdit ? () => { deleteItem(item.id); setEditingItemId(null); } : null}
                        onCancel={() => setEditingItemId(null)}
                        readOnly={!canEdit}
                        mandatoryLineFields={mandatoryLineFields || []}
                        hideSupplier
                      />
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-muted/30 font-bold">
              <td className="px-2 py-2" colSpan={2}>Items: {(order.line_items||[]).length}</td>
              <td colSpan={canEdit ? 8 : 7} className="text-right px-2 py-2">Total ({currency}):</td>
              <td className="text-right px-2 py-2 text-primary">{fmtTotal(order.net_total || order.total_value || 0)}</td>
              <td className="text-right px-2 py-2">{fmtTotal((order.line_items||[]).reduce((s,i) => s + (i.net_price||0)*((i.sales_tax||0)/100), 0))}</td>
              <td className="text-right px-2 py-2">{fmtTotal((order.line_items||[]).reduce((s,i) => { const n=i.net_price||0; return s+n+n*((i.sales_tax||0)/100); }, 0))}</td>
              {canEdit && <td></td>}
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}
